import { apiClient } from './client';
import { unwrapList, unwrapItem } from './unwrap';
import type {
  Competition,
  CompetitionContext,
  CompetitionLanding,
  CompetitionParticipant,
  ExhibitionSelection,
  ExhibitionSelectionSettings,
  PaymentMethod,
  PagedResult,
  UpdateCompetitionLandingPayload,
} from './types';

/**
 * Matches Aperture.Application.Competitions.Dtos.AssignParticipantRequest
 * exactly: AssignParticipantRequest(Guid? UserId, string? Email, string?
 * FirstName, string? LastName). Either an existing account (userId) or a
 * brand-new one to create on the fly (email/firstName/lastName).
 */
export interface AssignParticipantRequest {
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface CompetitionListParams {
  page?: number;
  pageSize?: number;
  organizationId?: string;
  /** Filters to competitions currently accepting self-service signup requests. */
  openForSignup?: boolean;
}

/** Matches Aperture.Application.Competitions.Dtos.CreateCompetitionRequest exactly - no status/dates accepted here. */
export interface CreateCompetitionPayload {
  organizationId: string;
  name: string;
  description?: string;
}

/** Matches Aperture.Application.Competitions.Dtos.UpdateCompetitionRequest exactly - status is never settable via this endpoint. */
export interface UpdateCompetitionPayload {
  name: string;
  description?: string | null;
  submissionOpensAt?: string | null;
  submissionDeadline?: string | null;
  judgingDeadline?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export const competitionsApi = {
  list: (params: CompetitionListParams = {}) =>
    apiClient
      .get('/competitions', { params })
      .then((r) => unwrapList<Competition>(r.data)) as Promise<PagedResult<Competition>>,

  get: (id: string) =>
    apiClient.get(`/competitions/${id}`).then((r) => unwrapItem<Competition>(r.data)),

  /** The competition behind a readable URL (its slug). */
  getBySlug: (slug: string) =>
    apiClient.get(`/competitions/slug/${encodeURIComponent(slug)}`).then((r) => unwrapItem<Competition>(r.data)),

  /** The announcement page - readable without signing in once the competition is no longer a draft. */
  getLanding: (id: string) =>
    apiClient.get<CompetitionLanding>(`/competitions/${id}/landing`).then((r) => r.data),

  getLandingBySlug: (slug: string) =>
    apiClient.get<CompetitionLanding>(`/competitions/slug/${encodeURIComponent(slug)}/landing`).then((r) => r.data),

  updateLanding: (id: string, payload: UpdateCompetitionLandingPayload) =>
    apiClient.put<CompetitionLanding>(`/competitions/${id}/landing`, payload).then((r) => r.data),

  /** The caller's relationship to this specific competition - see CompetitionContext. */
  getMyContext: (id: string) =>
    apiClient.get<CompetitionContext>(`/competitions/${id}/my-context`).then((r) => r.data),

  create: (payload: CreateCompetitionPayload) =>
    apiClient.post<Competition>('/competitions', payload).then((r) => r.data),

  update: (id: string, payload: UpdateCompetitionPayload) =>
    apiClient.put<Competition>(`/competitions/${id}`, payload).then((r) => r.data),

  /** Toggles whether competitors may self-service request to join this competition. */
  setOpenForSignup: (id: string, isOpenForSignup: boolean) =>
    apiClient.put<Competition>(`/competitions/${id}/open-for-signup`, { isOpenForSignup }).then((r) => r.data),

  /** Toggles whether this competition is held under the patronage of FZS (Fotografska zveza Slovenije). */
  setFzsPatronage: (id: string, isFzsPatronage: boolean) =>
    apiClient.put<Competition>(`/competitions/${id}/fzs-patronage`, { isFzsPatronage }).then((r) => r.data),

  /** Multipart upload of the announcement PDF - POST /competitions/{id}/announcement. */
  uploadAnnouncement: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<CompetitionLanding>(`/competitions/${id}/announcement`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  removeAnnouncement: (id: string) =>
    apiClient.delete<CompetitionLanding>(`/competitions/${id}/announcement`).then((r) => r.data),

  /** Multipart upload - matches POST /competitions/{id}/logo (CompetitionsController.UploadLogo). */
  uploadLogo: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<Competition>(`/competitions/${id}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  remove: (id: string, force = false) =>
    apiClient.delete(`/competitions/${id}`, { params: force ? { force: true } : undefined }).then((r) => r.data),

  // Lifecycle transitions - see CompetitionsController: draft -> scheduled ->
  // active -> review -> judging -> awarding -> completed -> archived. Each is a bodiless POST that
  // returns the updated CompetitionDto.
  schedule: (id: string) => apiClient.post<Competition>(`/competitions/${id}/schedule`).then((r) => r.data),
  activate: (id: string) => apiClient.post<Competition>(`/competitions/${id}/activate`).then((r) => r.data),
  startReview: (id: string) =>
    apiClient.post<Competition>(`/competitions/${id}/start-review`).then((r) => r.data),
  revertToReview: (id: string) =>
    apiClient.post<Competition>(`/competitions/${id}/revert-to-review`).then((r) => r.data),
  startJudging: (id: string) =>
    apiClient.post<Competition>(`/competitions/${id}/start-judging`).then((r) => r.data),
  startAwarding: (id: string) =>
    apiClient.post<Competition>(`/competitions/${id}/start-awarding`).then((r) => r.data),
  revertToJudging: (id: string) =>
    apiClient.post<Competition>(`/competitions/${id}/revert-to-judging`).then((r) => r.data),
  reopenAwarding: (id: string) =>
    apiClient.post<Competition>(`/competitions/${id}/reopen-awarding`).then((r) => r.data),
  /** Selects the best `percent` % of every category for the exhibition (awarding only). */
  setExhibitionSelection: (id: string, settings: ExhibitionSelectionSettings) =>
    apiClient.put<ExhibitionSelection>(`/competitions/${id}/exhibition-selection`, settings).then((r) => r.data),
  complete: (id: string) => apiClient.post<Competition>(`/competitions/${id}/complete`).then((r) => r.data),
  archive: (id: string) => apiClient.post<Competition>(`/competitions/${id}/archive`).then((r) => r.data),
  // Revert Review, Judging or Completed back to Active - see CompetitionsController.RevertToActive.
  revertToActive: (id: string) =>
    apiClient.post<Competition>(`/competitions/${id}/revert-to-active`).then((r) => r.data),

  // Competitor assignment - see CompetitionsController's participants
  // endpoints. A competitor only sees/enters a competition once they hold an
  // Approved participant row.
  listParticipants: (competitionId: string) =>
    apiClient
      .get(`/competitions/${competitionId}/participants`)
      .then((r) => unwrapList<CompetitionParticipant>(r.data)) as Promise<PagedResult<CompetitionParticipant>>,

  /** Organizer-direct assignment, instantly Approved. */
  assignParticipant: (competitionId: string, payload: AssignParticipantRequest) =>
    apiClient
      .post<CompetitionParticipant>(`/competitions/${competitionId}/participants`, payload)
      .then((r) => r.data),

  /** Self-service: the calling user joins a competition open for signup - at once, no approval. */
  requestToJoin: (competitionId: string) =>
    apiClient
      .post<CompetitionParticipant>(`/competitions/${competitionId}/participants/request`)
      .then((r) => r.data),

  /** Organizer marks whether a competitor has paid the entry fee. */
  setParticipantPaid: (competitionId: string, participantId: string, hasPaid: boolean, method?: PaymentMethod) =>
    apiClient
      .put<CompetitionParticipant>(`/competitions/${competitionId}/participants/${participantId}/paid`, { hasPaid, method })
      .then((r) => r.data),

  removeParticipant: (competitionId: string, participantId: string) =>
    apiClient.delete(`/competitions/${competitionId}/participants/${participantId}`).then((r) => r.data),
};
